import type { UIMessageChunk } from 'ai';
import type { IAiChatToolOutput } from '../interfaces/IAiChatTransportOptions';

/**
 * Passes a reply stream through untouched, and reports each tool call the moment its output goes by.
 *
 * The output chunk names the call, not the tool: the name is on the chunk that opened the call, so
 * it is kept per call until the output arrives. A preliminary output is skipped — the final one
 * follows on the same call.
 *
 * A listener that throws is reported and the reply goes on: what a tool output makes the app do
 * is beside the reply, and must not end it.
 */
const observeToolOutputs = (
  stream: ReadableStream<UIMessageChunk>,
  onToolOutput: (toolOutput: IAiChatToolOutput) => void,
): ReadableStream<UIMessageChunk> => {
  const toolNames = new Map<string, string>();

  return stream.pipeThrough(new TransformStream<UIMessageChunk, UIMessageChunk>({
    transform(chunk, controller) {
      if (chunk.type === 'tool-input-start' || chunk.type === 'tool-input-available') {
        toolNames.set(chunk.toolCallId, chunk.toolName);
      }

      if (chunk.type === 'tool-output-available' && !chunk.preliminary) {
        try {
          onToolOutput({
            toolCallId: chunk.toolCallId,
            toolName: toolNames.get(chunk.toolCallId) ?? '',
            output: chunk.output,
          });
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('AI chat tool output listener failed', error);
        }
      }

      controller.enqueue(chunk);
    },
  }));
};

export default observeToolOutputs;
